import React, { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import CustomerServices from "../../../services/CustomerServices";
import fire from "../../../utils/Toastr";
import { HollowDotsSpinner } from "react-epic-spinners";

export default function ApproveAccountModal({ isOpen, onClose, approvalDetails, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState("Approve");
  const [comment, setComment] = useState("");

  const handleApprove = async () => {
    setLoading(true);
    
    const payload = {
      accountNumber: approvalDetails?.oldValue?.accountNumber,
      action: action,
      comment: comment,
      accountIdentifier: approvalDetails?.approvalIdentifier
    };

    try {
      const response = await CustomerServices.approveOrRejectAccountProfileUpdate(payload);
      if (response.status) {
        fire("success", response.message);
        onSuccess();
        onClose();
      } else {
        fire("error", response.message || `Failed to ${action.toLowerCase()} account profile update`);
      }
    } catch (error) {
      fire("error", `An error occurred while ${action.toLowerCase()}ing account profile update`);
    } finally {
      setLoading(false);
    }
  };

  if (!approvalDetails) return null;

  // Check if this is an initiated request that can be acted upon
  const canAct = approvalDetails.status === "Initiated";

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900 mb-4">
                      {canAct ? "Review Account Profile Update Request" : "Account Profile Update Details"}
                    </Dialog.Title>
                    
                    <div className="space-y-4">
                      {/* Status Banner */}
                      <div className={`border-l-4 p-4 ${
                        approvalDetails.status === "Initiated" 
                          ? "bg-yellow-50 border-yellow-400" 
                          : approvalDetails.status === "Approved"
                          ? "bg-green-50 border-green-400"
                          : "bg-red-50 border-red-400"
                      }`}>
                        <div className="flex">
                          <div className="ml-3">
                            <p className={`text-sm ${
                              approvalDetails.status === "Initiated" 
                                ? "text-yellow-700" 
                                : approvalDetails.status === "Approved"
                                ? "text-green-700"
                                : "text-red-700"
                            }`}>
                              <span className="font-medium">Status: {approvalDetails.status}</span>
                            </p>
                            <p className="text-sm text-gray-600 mt-1">
                              Account Number: <span className="font-medium">{approvalDetails.oldValue?.accountNumber || "-"}</span>
                            </p>
                            <p className="text-sm text-gray-600">
                              Initiated By: <span className="font-medium">{approvalDetails.initiatedBy}</span>
                            </p>
                            <p className="text-sm text-gray-600">
                              Date Initiated: <span className="font-medium">{new Date(approvalDetails.dateInitiated).toLocaleString()}</span>
                            </p>
                            {approvalDetails.approvedBy && (
                              <p className="text-sm text-gray-600">
                                {approvalDetails.status === "Approved" ? "Approved" : "Rejected"} By: <span className="font-medium">{approvalDetails.approvedBy}</span>
                              </p>
                            )}
                            {approvalDetails.dateApproved && approvalDetails.dateApproved !== "0001-01-01T00:00:00" && (
                              <p className="text-sm text-gray-600">
                                Date {approvalDetails.status === "Approved" ? "Approved" : "Rejected"}: <span className="font-medium">{new Date(approvalDetails.dateApproved).toLocaleString()}</span>
                              </p>
                            )}
                            <p className="text-sm text-gray-600 mt-2">
                              Reason for Change: <span className="font-medium">{approvalDetails.reasonForChange}</span>
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Comparison Grid */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="border rounded-lg p-4 bg-red-50">
                          <h4 className="font-semibold text-red-600 mb-2">Current Values</h4>
                          <div className="space-y-2">
                            <p><span className="text-sm font-medium">Account Number:</span> {approvalDetails.oldValue?.accountNumber || "-"}</p>
                            <p><span className="text-sm font-medium">Account Name:</span> {approvalDetails.oldValue?.accountName || "N/A"}</p>
                          </div>
                        </div>

                        <div className="border rounded-lg p-4 bg-green-50">
                          <h4 className="font-semibold text-green-600 mb-2">New Values</h4>
                          <div className="space-y-2">
                            <p><span className="text-sm font-medium">Account Number:</span> {approvalDetails.oldValue?.accountNumber || "-"}</p>
                            <p><span className="text-sm font-medium">Account Name:</span> {approvalDetails.newValue?.accountName || "N/A"}</p>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons - Only show for initiated requests */}
                      {canAct && (
                        <>
                          <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700">Action</label>
                            <select
                              value={action}
                              onChange={(e) => setAction(e.target.value)}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            >
                              <option value="Approve">Approve</option>
                              <option value="Reject">Reject</option>
                            </select>
                          </div>

                          <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700">Comment</label>
                            <textarea
                              value={comment}
                              onChange={(e) => setComment(e.target.value)}
                              rows={3}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                              placeholder="Enter comment (optional)"
                            />
                          </div>

                          <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                            {loading ? (
                              <div className="flex justify-center w-full">
                                <HollowDotsSpinner size={30} color="#4D627E" />
                              </div>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  onClick={handleApprove}
                                  className={`inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm sm:ml-3 sm:w-auto ${
                                    action === "Approve" 
                                      ? "bg-green-600 hover:bg-green-500" 
                                      : "bg-red-600 hover:bg-red-500"
                                  }`}
                                >
                                  {action}
                                </button>
                                <button
                                  type="button"
                                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                  onClick={onClose}
                                >
                                  Close
                                </button>
                              </>
                            )}
                          </div>
                        </>
                      )}

                      {/* Close button for non-initiated requests */}
                      {!canAct && (
                        <div className="mt-5 sm:mt-4 sm:flex sm:justify-end">
                          <button
                            type="button"
                            className="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:w-auto"
                            onClick={onClose}
                          >
                            Close
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}